// Хранилище фоновых загрузок ролика: задача и её прогресс живут ВНЕ компонента,
// поэтому уход из видео-блока больше не обрывает загрузку (раньше `VideoUpload`
// отменял её на размонтировании). Обрывается она только вместе со страницей.
// Чистый модуль без React: провайдер (`VideoUploadContext`) лишь подаёт зависимости.
import type { BlockUploadTarget } from './uploadTask'

export type VideoSlot = 'videoId' | 'videoIdEn'

/** Место загрузки ролика. `key` — идентичность места на экране: у блочной цели
 *  `placeKey(block)` (блок + слот языка), без блока — id экземпляра компонента. */
export interface VideoUploadTarget {
  key: string
  /** Блок-владелец: по нему ролик сохраняется в фоне, когда места на экране нет. */
  block?: BlockUploadTarget
}

export interface VideoUploadDeps {
  upload: (
    file: File,
    title: string,
    onProgress: (percent: number) => void,
    signal: AbortSignal,
  ) => Promise<string>
  /** Канонический ключ места — редактор сверяет его через алиасы id: смена
   *  источника чтения (черновик ⇄ живой курс) меняет id того же блока. */
  canon: (target: VideoUploadTarget) => string
  /** Канонический id урока: черновик живого курса меняет id того же урока. */
  canonLesson?: (lessonId: number) => number
  /** Места на экране нет — сохранить ролик в свежий серверный блок. */
  commitBackground: (block: BlockUploadTarget, videoId: string) => unknown
  onError: (error: unknown) => void
}

interface Task {
  target: VideoUploadTarget
  progress: number
  /** Брошен на холст: полоса — там, и после возврата в блок тоже. */
  fromCanvas: boolean
  controller: AbortController
}

interface Consumer {
  target: VideoUploadTarget
  onUploaded: (videoId: string) => void
}

export interface VideoUploadStore {
  subscribe: (listener: () => void) => () => void
  /** Прогресс загрузки в это место (`null` — загрузки нет). */
  progressOf: (target: VideoUploadTarget) => number | null
  /** Загрузку в это место бросили на холст (полоса — на холсте, не в панели). */
  fromCanvasOf: (target: VideoUploadTarget) => boolean
  /** Место на экране: пока оно зарегистрировано, ролик уходит в его черновик. */
  attach: (target: VideoUploadTarget, onUploaded: (videoId: string) => void) => () => void
  start: (
    target: VideoUploadTarget,
    file: File,
    title: string,
    fromCanvas?: boolean,
  ) => Promise<void>
  abortAll: () => void
  /** Блок удалён: его ролики некуда класть, загрузку незачем продолжать.
   *  `true`, если отменена хотя бы одна загрузка: упавшее удаление должно сказать автору,
   *  что ролик уже потерян. */
  abortBlock: (blockId: number) => boolean
  /** Урок удалён: то же для всех его блоков. */
  abortLesson: (lessonId: number) => boolean
}

/** Зависимости читаются В МОМЕНТ использования (`getDeps`): задача переживает
 *  рендер, в котором её поставили, а гейты и алиасы к коммиту уже другие. */
export const createVideoUploadStore = (getDeps: () => VideoUploadDeps): VideoUploadStore => {
  const tasks = new Set<Task>()
  const consumers = new Set<Consumer>()
  const listeners = new Set<() => void>()
  const emit = () => listeners.forEach((listener) => listener())
  const same = (a: VideoUploadTarget, b: VideoUploadTarget) => {
    const { canon } = getDeps()
    return canon(a) === canon(b)
  }
  const find = (target: VideoUploadTarget) => [...tasks].find((t) => same(t.target, target))

  // Куда уйдёт ролик, решается В МОМЕНТ окончания загрузки: за минуты заливки
  // автор как раз и уходит (или возвращается).
  const deliver = (task: Task, videoId: string) => {
    const live = [...consumers].find((c) => same(c.target, task.target))
    if (live) live.onUploaded(videoId)
    else if (task.target.block) void getDeps().commitBackground(task.target.block, videoId)
  }

  const start = async (
    target: VideoUploadTarget,
    file: File,
    title: string,
    fromCanvas = false,
  ) => {
    // Одно место — одна загрузка: зона погашена, пока идёт прогресс.
    if (find(target)) return
    const task: Task = { target, progress: 0, fromCanvas, controller: new AbortController() }
    tasks.add(task)
    emit()
    try {
      const onProgress = (percent: number) => {
        task.progress = percent
        emit()
      }
      const id = await getDeps().upload(file, title, onProgress, task.controller.signal)
      // Загрузка могла кончиться в тот же миг, когда блок удалили: ролик класть некуда.
      if (!task.controller.signal.aborted) deliver(task, id)
    } catch (e) {
      if (!task.controller.signal.aborted) getDeps().onError(e)
    } finally {
      tasks.delete(task)
      emit()
    }
  }

  const abortWhere = (matches: (t: Task) => boolean): boolean => {
    let aborted = false
    tasks.forEach((t) => {
      if (!matches(t)) return
      t.controller.abort()
      aborted = true
    })
    return aborted
  }

  return {
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    progressOf: (target) => find(target)?.progress ?? null,
    fromCanvasOf: (target) => find(target)?.fromCanvas ?? false,
    attach: (target, onUploaded) => {
      const entry: Consumer = { target, onUploaded }
      consumers.add(entry)
      return () => consumers.delete(entry)
    },
    start,
    abortAll: () => tasks.forEach((t) => t.controller.abort()),
    // Сравнение через `canon`: задача могла быть поставлена под другим id того же блока
    // (временный, черновик ⇄ живой курс).
    abortBlock: (blockId) =>
      abortWhere((t) => {
        if (!t.target.block) return false
        const deleted = { ...t.target, block: { ...t.target.block, blockId } }
        return same(t.target, deleted)
      }),
    abortLesson: (lessonId) =>
      abortWhere((t) => {
        // Не через `canon`: ключ места урока не содержит.
        const canonLesson = getDeps().canonLesson ?? ((id: number) => id)
        return !!t.target.block && canonLesson(t.target.block.lessonId) === canonLesson(lessonId)
      }),
  }
}
